import React, { createContext, useContext, useState, useEffect } from 'react';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

// Translation data
const translations = {
  en: {
    // Navigation
    dashboard: 'Dashboard',
    overview: 'Overview',
    meters: 'Meters',
    users: 'Users',
    transactions: 'Transactions',
    settings: 'Settings',
    logout: 'Logout',

    // Settings
    appearance: 'Appearance',
    language: 'Language',
    notifications: 'Notifications',
    preferences: 'Preferences',
    theme: 'Theme',
    lightMode: 'Light Mode',
    darkMode: 'Dark Mode',
    autoMode: 'Auto (System)',
    selectLanguage: 'Select Language',
    english: 'English',
    french: 'French',
    spanish: 'Spanish',
    german: 'German',
    chinese: 'Chinese',
    japanese: 'Japanese',
    emailNotifications: 'Email Notifications',
    pushNotifications: 'Push Notifications',
    smsNotifications: 'SMS Notifications',
    marketingEmails: 'Marketing Emails',
    autoRefresh: 'Auto Refresh',
    compactView: 'Compact View',
    showAnimations: 'Show Animations',
    highContrast: 'High Contrast',
    resetToDefaults: 'Reset to Defaults',
    saveSettings: 'Save Settings',
    settingsSaved: 'Settings saved successfully!',
    generalSettings: 'General Settings',
    accountSettings: 'Account Settings',
    securitySettings: 'Security Settings',
    privacySettings: 'Privacy Settings',
    updateAccount: 'Update Account',
    changePassword: 'Change Password',
    twoFactorAuth: 'Two-Factor Authentication',
    dataPrivacy: 'Data & Privacy',
    exportData: 'Export Data',
    deleteAccount: 'Delete Account',
    manageAccount: 'Manage account settings',
    changePasswordDesc: 'Update your password regularly',
    enable2FA: 'Add extra security layer',
    managePrivacy: 'Control your data and privacy',
    downloadData: 'Download your data',
    deleteAccountDesc: 'Permanently delete your account'
  },
  fr: {
    dashboard: 'Tableau de Bord',
    overview: 'Aperçu',
    meters: 'Compteurs',
    users: 'Utilisateurs',
    transactions: 'Transactions',
    settings: 'Paramètres',
    logout: 'Déconnexion',
    appearance: 'Apparence',
    language: 'Langue',
    notifications: 'Notifications',
    preferences: 'Préférences',
    theme: 'Thème',
    lightMode: 'Mode Clair',
    darkMode: 'Mode Sombre',
    autoMode: 'Auto (Système)',
    selectLanguage: 'Sélectionner la Langue',
    english: 'Anglais',
    french: 'Français',
    spanish: 'Espagnol',
    german: 'Allemand',
    chinese: 'Chinois',
    japanese: 'Japonais',
    emailNotifications: 'Notifications Email',
    pushNotifications: 'Notifications Push',
    smsNotifications: 'Notifications SMS',
    marketingEmails: 'Emails Marketing',
    autoRefresh: 'Actualisation Auto',
    compactView: 'Vue Compacte',
    showAnimations: 'Afficher Animations',
    highContrast: 'Contraste Élevé',
    resetToDefaults: 'Réinitialiser',
    saveSettings: 'Sauvegarder',
    settingsSaved: 'Paramètres sauvegardés!',
    generalSettings: 'Paramètres Généraux',
    accountSettings: 'Paramètres Compte',
    securitySettings: 'Paramètres Sécurité',
    privacySettings: 'Paramètres Vie Privée',
    updateAccount: 'Mettre à Jour Compte',
    changePassword: 'Changer Mot de Passe',
    twoFactorAuth: 'Authentification 2 Facteurs',
    dataPrivacy: 'Données & Vie Privée',
    exportData: 'Exporter Données',
    deleteAccount: 'Supprimer Compte',
    manageAccount: 'Gérer paramètres compte',
    changePasswordDesc: 'Mettez à jour votre mot de passe régulièrement',
    enable2FA: 'Ajouter couche sécurité supplémentaire',
    managePrivacy: 'Contrôler vos données et vie privée',
    downloadData: 'Télécharger vos données',
    deleteAccountDesc: 'Supprimer définitivement votre compte'
  },
  es: {
    dashboard: 'Panel de Control',
    overview: 'Resumen',
    meters: 'Contadores',
    users: 'Usuarios',
    transactions: 'Transacciones',
    settings: 'Configuración',
    logout: 'Cerrar Sesión',
    appearance: 'Apariencia',
    language: 'Idioma',
    notifications: 'Notificaciones',
    preferences: 'Preferencias',
    theme: 'Tema',
    lightMode: 'Modo Claro',
    darkMode: 'Modo Oscuro',
    autoMode: 'Auto (Sistema)',
    selectLanguage: 'Seleccionar Idioma',
    english: 'Inglés',
    french: 'Francés',
    spanish: 'Español',
    german: 'Alemán',
    chinese: 'Chino',
    japanese: 'Japonés',
    emailNotifications: 'Notificaciones Email',
    pushNotifications: 'Notificaciones Push',
    smsNotifications: 'Notificaciones SMS',
    marketingEmails: 'Emails Marketing',
    autoRefresh: 'Actualización Auto',
    compactView: 'Vista Compacta',
    showAnimations: 'Mostrar Animaciones',
    highContrast: 'Alto Contraste',
    resetToDefaults: 'Restablecer',
    saveSettings: 'Guardar',
    settingsSaved: '¡Configuración guardada!',
    generalSettings: 'Configuración General',
    accountSettings: 'Configuración Cuenta',
    securitySettings: 'Configuración Seguridad',
    privacySettings: 'Configuración Privacidad',
    updateAccount: 'Actualizar Cuenta',
    changePassword: 'Cambiar Contraseña',
    twoFactorAuth: 'Autenticación 2 Factores',
    dataPrivacy: 'Datos y Privacidad',
    exportData: 'Exportar Datos',
    deleteAccount: 'Eliminar Cuenta',
    manageAccount: 'Administrar configuración cuenta',
    changePasswordDesc: 'Actualice su contraseña regularmente',
    enable2FA: 'Agregar capa de seguridad adicional',
    managePrivacy: 'Controlar sus datos y privacidad',
    downloadData: 'Descargar sus datos',
    deleteAccountDesc: 'Eliminar permanentemente su cuenta'
  }
};

export const LanguageProvider = ({ children }) => {
  const [currentLanguage, setCurrentLanguage] = useState(() => {
    return localStorage.getItem('language') || 'en';
  });

  const t = (key) => {
    return translations[currentLanguage]?.[key] || translations.en[key] || key;
  };

  const changeLanguage = (language) => {
    setCurrentLanguage(language);
    localStorage.setItem('language', language);
  };

  const getAvailableLanguages = () => {
    return Object.keys(translations).map(code => ({
      code,
      name: translations[code].selectLanguage || code.toUpperCase(),
      nativeName: translations[code][code] || translations.en[code] || code.toUpperCase()
    }));
  };

  const value = {
    currentLanguage,
    t,
    changeLanguage,
    getAvailableLanguages,
    translations: translations[currentLanguage] || translations.en
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export default LanguageContext;